import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
import { MaterialUiIcon } from '../../icon';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import {
    businessServiceFilterId,
    siteFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';

const title = t`Service Tests`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'test',
        entityFields: 'name,tags',
        metrics: ['status', 'statusCounts'],
        trends: ['status'],
        renderAsEntity: 'true',
    },
};

const DEFAULT_COLUMNS = [
    {
        type: COLUMN_TYPES.ICON,
        title: t`Type`,
        mapping: 'test.testType',
        sortable: true,
        width: 74,
        csvFormatter: (value, row) => row?.test?.typeNode?.details?.title,
        icons: [
            {
                getIcon: (row) => {
                    const { icon } = row?.test?.typeNode?.details || {};
                    return <MaterialUiIcon icon={icon} />;
                },
                getTooltip: (row) => row?.test?.typeNode?.details?.title,
            },
        ],
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Service Test`,
        sortable: true,
        mapping: 'test.name',
        width: 250,
        link: {
            target: '/test-detail',
            mappedFilterParams: {
                [testFilterId]: 'test',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Tags`,
        mapping: 'test.tags',
        sortable: false,
        formatter: (tags) => {
            if (isEmpty(tags)) return DEFAULT_INVALID_VALUE;
            return tags.join(', ');
        },
    },
    {
        title: t`Interface`,
        width: 100,
        mapper: (row) => {
            if (row.test.adapterType === 'wireless') {
                return 'Wireless';
            }
            if (row.test.adapterType === 'multi') {
                return 'Wireless';
            }
            return 'Wired';
        },
    },
    {
        title: t`Impacted nPoints`,
        width: 160,
        sortable: true,
        align: 'center',
        sortProperty: 'DIVIDE(statusCounts.affected, statusCounts.total)',
        mapper: (row) => `${row.statusCounts?.affected} / ${row.statusCounts?.total}`,
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
    },
    {
        type: COLUMN_TYPES.TESTLOGLINK,
        mapping: 'test',
        width: 110,
        uiOnly: true,
        link: {
            target: '/test-log',
            mappedFilterParams: {
                [testFilterId]: 'test',
            },
        },
    },
];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'test',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton test={row?.test} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

const sorters = [{ property: 'status', direction: 'desc' }];

function TestListPanel(props) {
    const loader = useQueryLoader(dataSource);
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);

    useEffect(() => {
        if (hasNg1DrillDownPrivilege()) {
            setColumns([...DEFAULT_COLUMNS, NG1_DRILLDOWN_COLUMN]);
        }
    }, [hasNg1DrillDownPrivilege]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="test.deleted"
        />
    );
}

TestListPanel.propTypes = panelPropTypes;

TestListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'TestList',
    catalogEntry: {
        category: PANEL_CATEGORIES.TEST,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of service tests, showing status trends and impacted nPoint counts`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, statusFilterId, siteFilterId, testTypeFilterId],
    },
})(TestListPanel);
